/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// React
import React from "react";
// redux hooks
import { useFieldArray, Controller } from "react-hook-form";
// BASE components
import BASESvgIcon from '../../../../../../../@BASE/core/BASESvgIcon';
import BASEUtils from "../../../../../../../@BASE/utils/BASEUtils";
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from "@mui/system/Box";
import Button from "@mui/material/Button";
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from "@mui/material/Typography";
// custom components
import NumberedHeading from "../../../../../../shared-components/forms/NumberedHeading";
import WYSIWYGEditor from "../../../../../../shared-components/WYSIWYGEditor";
// data
import { ReviewData } from "../../../data/Defaults";

export default function FlashCardArray({ control, register, setValue, getValues, errors, inputDir }) {
  // setup field array
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'content.core',
  });

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" sx={{marginTop: '22px'}} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="py-0">
                <Accordion
                  defaultExpanded
                  className="bg-transparent shadow-none"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography className="grow m-0 font-semibold">
                      Flash Card {index + 1}
                    </Typography>
                    {index !== 0 &&
                      <IconButton 
                        className="ml-16 mr-8"
                        aria-label="move up"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index - 1);
                        }}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>
                      </IconButton>
                    }
                    {index !== fields.length - 1 &&
                      <IconButton 
                        className="mx-8"
                        aria-label="move down"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index + 1);
                        }}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>
                      </IconButton>
                    }
                    <IconButton
                      className="ml-8 mr-16"
                      aria-label="delete"
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    >
                      <BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>
                    </IconButton>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box
                      className="p-0"
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} lg={6}>
                          <NumberedHeading number="1" title="Term (TL)" instructions='Enter the term or phrase in the target language.' required />
                          <Controller
                            control={control}
                            name={`content.core.${index}.term_tl`}
                            render={({ field }) => (
                              <>
                                <TextField
                                  {...field}
                                  className="mt-16 mb-16"
                                  label="Term (TL)"
                                  placeholder="Term (TL)"
                                  variant="outlined"
                                  fullWidth
                                  error={!!errors.content?.cards?.[index]?.term_tl}
                                  helperText={errors.content?.cards?.[index]?.term_tl?.message}
                                  required
                                  dir={inputDir}
                                  size="small"
                                />
                              </>
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                          <NumberedHeading number="2" title="Term (EN)" instructions='Enter the term or phrase in English.' required />
                          <Controller
                            control={control}
                            name={`content.core.${index}.term_en`}
                            render={({ field }) => (
                              <>
                                <TextField
                                  {...field}
                                  className="mt-16 mb-16"
                                  label="Term (EN)"
                                  placeholder="Term (EN)"
                                  variant="outlined"
                                  fullWidth
                                  error={!!errors.content?.cards?.[index]?.term_en}
                                  helperText={errors.content?.cards?.[index]?.term_en?.message}
                                  required
                                  size="small"
                                />
                              </>
                            )}
                          />
                        </Grid>
                      </Grid>

                      <Divider className="mt-16" />

                      <NumberedHeading number="3" title="Definition" instructions="Enter the term's definition." required />
                      <Controller
                        control={control}
                        name={`content.core.${index}.definition`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="Definition"
                              placeholder="Definition"
                              error={!!errors.content?.cards?.[index]?.definition}
                              helperText={errors.content?.cards?.[index]?.definition?.message}
                              variant="outlined"
                              fullWidth 
                              multiline
                              minRows={1}
                              maxRows={5}
                              required
                              dir={inputDir}
                              size="small"
                            />
                          </>
                        )}
                      />
                      
                      <Divider className="mt-16" />
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} lg={6}>
                          <NumberedHeading number="4" title="Synonym" instructions="Enter a synonym for the term or phrase." required />
                          <Controller
                            control={control}
                            name={`content.core.${index}.synonym`}
                            render={({ field }) => (
                              <>
                                <TextField
                                  {...field}
                                  className="mt-16 mb-16"
                                  label="Synonym"
                                  placeholder="Synonym"
                                  variant="outlined"
                                  fullWidth
                                  error={!!errors.content?.cards?.[index]?.synonym}
                                  helperText={errors.content?.cards?.[index]?.synonym?.message}
                                  required
                                  dir={inputDir}
                                  size="small"
                                />
                              </>
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                          <NumberedHeading number="5" title="Antonym" instructions="Enter a antonym for the term or phrase." required />
                          <Controller
                            control={control}
                            name={`content.core.${index}.antonym`}
                            render={({ field }) => (
                              <>
                                <TextField
                                  {...field}
                                  className="mt-16 mb-16"
                                  label="Antonym"
                                  placeholder="Antonym"
                                  variant="outlined"
                                  fullWidth
                                  error={!!errors.content?.cards?.[index]?.antonym}
                                  helperText={errors.content?.cards?.[index]?.antonym?.message}
                                  required
                                  dir={inputDir}
                                  size="small"
                                />
                              </>
                            )}
                          />
                        </Grid>
                      </Grid>

                      <Divider className="mt-16" />

                      <NumberedHeading number="6" title="Pronunciation" instructions="Please write how the word is pronunciated such as uhg-zam-pl for the word 'example'." required />
                      <Controller
                        control={control}
                        name={`content.core.${index}.pronunciation`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="Pronunciation"
                              placeholder="Pronunciation"
                              variant="outlined"
                              fullWidth
                              error={!!errors.content?.cards?.[index]?.pronunciation}
                              helperText={errors.content?.cards?.[index]?.pronunciation?.message}
                              required
                              dir={inputDir}
                              size="small"
                            />
                          </>
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading number="7" title="Sentence" instructions="Enter a sentence that uses the term or phrase." required />
                      <Controller
                        control={control}
                        name={`content.core.${index}.sentence`}
                        render={({ field }) => <WYSIWYGEditor {...field} error={errors.content?.cards?.[index]?.sentence} />}
                      />

                      <Divider className="mt-16" />
                      
                    </Box>
                  </AccordionDetails>
                  <AccordionActions>
                    {index !== 0 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index - 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>}
                        size="small"
                      >
                        Move Up
                      </Button>
                    }
                    {index !== fields.length - 1 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index + 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>}
                        size="small"
                      >
                        Move Down
                      </Button>
                    }
                    <Button
                      variant="contained"
                      onClick={() => remove(index)}
                      color="error"
                      startIcon={<BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>}
                      size="small"
                    >
                      Remove
                    </Button>
                  </AccordionActions>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-8 rounded cursor-pointer"
          color="secondary"
          variant="contained"
          onClick={() => append({ 
            id: BASEUtils.generateGUID(),
            review: ReviewData,
            term_tl: "",
            term_en: "",
            definition: "",
            synonym: "",
            antonym: "",
            sentence: "",
            pronunciation: "",
          })}
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add card
          </span>
        </Button>
      </Box>
    </>
  );
}
